import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { PasswordRequirementsTooltip } from "@/modules/shared/ui/components/password-requirements-tooltip"
import { useDispatch } from "react-redux"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Input } from "@/modules/shared/ui/components/input"
import { Button } from "@/modules/shared/ui/components/button"
import { Checkbox } from "@/modules/shared/ui/components/checkbox"
import { apiClient as api } from "@/lib/api-client"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/modules/shared/ui/components/form"
import { useAuth } from "@/modules/auth/hooks/use-auth"
import { AppRole } from "@/modules/auth/constants/app-role"
import {
  completeInvite,
  getInviteInfo,
  InviteInfoResponse,
} from "@/modules/auth/invite/infrastructure/invite-service"
import { LoginAd } from "@/modules/auth/login/ui/components/LoginAd"
import { FaEye, FaEyeSlash } from "react-icons/fa"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { useRecaptcha } from "@/modules/shared/hooks/use-recaptcha"
import { AppDispatch } from "@/store"
import { restoreAuth } from "@/modules/auth/store/authSlice"
import type { AuthUser } from "@/modules/auth/entities/AuthUser"

import { broadcastLogin } from "@/lib/session-sync"

const formSchema = z
  .object({
    tempPassword: z.string().min(1, {
      message: "Temporary password is required.",
    }),
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number")
      .regex(
        /[!@#$%^&*]/,
        "Password must contain at least one special character (!@#$%^&*)",
      ),
    confirmPassword: z.string().min(1, {
      message: "Please confirm your password.",
    }),
    agreedPrivacyAndTerms: z.boolean().refine((val) => val === true, {
      message: "You must agree to the Terms & Conditions and Privacy Policy.",
    }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
  })
  .refine((data) => data.newPassword !== data.tempPassword, {
    message: "New password must be different from temporary password.",
    path: ["newPassword"],
  })

type FormValues = z.infer<typeof formSchema>

const existingUserSchema = z.object({
  password: z.string().min(1, { message: "Password is required." }),
})

type ExistingUserFormValues = z.infer<typeof existingUserSchema>

export default function InviteEmployeePage() {
  const [formError, setFormError] = useState("")
  const [isSuccess, setIsSuccess] = useState(false)
  const [showTempPassword, setShowTempPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showExistingPassword, setShowExistingPassword] = useState(false)

  const [inviteInfo, setInviteInfo] = useState<InviteInfoResponse | null>(null)
  const [isLoadingInfo, setIsLoadingInfo] = useState(false)
  const [infoError, setInfoError] = useState("")
  const [legalDocs, setLegalDocs] = useState<{
    terms?: { version: string }
    privacy?: { version: string }
  } | null>(null)

  const [searchParams] = useSearchParams()
  const email = searchParams.get("email") ?? ""
  const userId = searchParams.get("userId") ?? ""
  const employeeName = searchParams.get("name") ?? ""
  const orgName = searchParams.get("org") ?? ""

  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { isAuthenticated, hasRole, loginWithCredentials } = useAuth()
  const { setOrganizationById } = useOrganizationsService()
  const { getToken } = useRecaptcha()

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tempPassword: "",
      newPassword: "",
      confirmPassword: "",
      agreedPrivacyAndTerms: false,
    },
  })

  const existingUserForm = useForm<ExistingUserFormValues>({
    resolver: zodResolver(existingUserSchema),
    defaultValues: { password: "" },
  })

  useEffect(() => {
    if (isSuccess) return
    if (isAuthenticated && hasRole(AppRole.Employee)) {
      navigate("/employee/dashboard", { replace: true })
    }
  }, [isAuthenticated, hasRole, navigate, isSuccess])

  useEffect(() => {
    if (!email || !userId) return
    setIsLoadingInfo(true)
    getInviteInfo(userId, email)
      .then((info) => setInviteInfo(info))
      .catch((err: unknown) => {
        const msg =
          err instanceof Error ? err.message : "Could not load invite details."
        setInfoError(msg)
      })
      .finally(() => setIsLoadingInfo(false))
  }, [email, userId])

  useEffect(() => {
    api
      .get("/legal-documents")
      .then((response) => setLegalDocs(response.data))
      .catch(() => setLegalDocs(null))
  }, [])

  const handleSubmit = async (values: FormValues) => {
    if (!email || !userId) {
      setFormError(
        "Invalid or expired invite link. Ask your HR team to resend it.",
      )
      return
    }

    if (!legalDocs?.terms?.version || !legalDocs?.privacy?.version) {
      setFormError(
        "Legal documents are still loading. Please try again in a moment.",
      )
      return
    }

    setFormError("")
    try {
      const captchaToken = await getToken("complete_invite")
      const response = await completeInvite({
        email,
        inviteToken: userId,
        tempPassword: values.tempPassword.trim(),
        newPassword: values.newPassword.trim(),
        captchaToken,
        acceptedTermsVersion: legalDocs.terms.version,
        acceptedPrivacyVersion: legalDocs.privacy.version,
      })

      if (!response) {
        setFormError("Invalid response from server. Please try again.")
        return
      }

      const authUser: AuthUser = {
        user: {
          id: response.user.id,
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          email: response.user.email,
          status: response.user.status,
          createdAt: response.user.createdAt,
          lastLoginAt: response.user.lastLoginAt,
          needsLegalConsent: response.user.needsLegalConsent,
          organizationRole: response.user.organizationRole,
          organizationRoles: response.user.organizationRoles,
        },
        roles: response.roles,
        permissions: response.permissions,
        currentOrganizationId: response.user.currentOrganizationId ?? null,
      }

      dispatch(restoreAuth({ user: authUser, loginTimestamp: Date.now() }))
      broadcastLogin(email)
      if (authUser.currentOrganizationId) {
        setOrganizationById(authUser.currentOrganizationId)
      }

      setIsSuccess(true)
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to complete invite. Please try again."

      if (message === "Password is incorrect.") {
        form.setError("tempPassword", {
          type: "server",
          message: "Incorrect temporary password.",
        })
        return
      }

      setFormError(message)
    }
  }

  const handleConfirm = () => {
    navigate("/employee/dashboard", { replace: true })
  }

  const handleExistingUserSubmit = async (values: ExistingUserFormValues) => {
    if (!email || !userId) {
      setFormError(
        "Invalid or expired invite link. Ask your HR team to resend it.",
      )
      return
    }
    setFormError("")
    try {
      const captchaToken = await getToken("login")
      const result = await loginWithCredentials(
        email,
        values.password,
        userId,
        captchaToken,
      )
      if (result?.user?.currentOrganizationId) {
        setOrganizationById(result.user.currentOrganizationId)
      }
      navigate("/employee/dashboard", { replace: true })
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Login failed. Please try again."
      setFormError(message)
    }
  }

  const isBusy = form.formState.isSubmitting

  if (isLoadingInfo) {
    return (
      <div className="flex flex-col md:flex-row w-full min-h-screen">
        <LoginAd />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-muted-foreground text-sm">
            Loading invite details…
          </div>
        </div>
      </div>
    )
  }

  if (infoError) {
    return (
      <div className="flex flex-col md:flex-row w-full min-h-screen">
        <LoginAd />
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-card rounded-lg p-8 shadow-sm border border-border max-w-md w-full">
            <p className="text-red-600 text-sm">{infoError}</p>
            <p className="mt-2 text-muted-foreground text-sm">
              Please contact your HR team to resend the invite.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col md:flex-row w-full min-h-screen">
      <LoginAd />
      <div className="flex-1 flex items-center justify-start overflow-y-auto">
        <div className="w-full max-w-full pr-4 md:pl-30 py-8">
          {isSuccess ? (
            <div className="w-full md:w-[75%] justify-center items-center">
              <div className="bg-card rounded-lg p-8 shadow-sm border border-border">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                    <svg
                      className="h-6 w-6 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    Welcome Aboard, {employeeName || "Employee"}!
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Your account has been successfully set up. You're all ready
                    to access your employee dashboard.
                  </p>
                  <Button
                    onClick={handleConfirm}
                    className="w-full h-11 text-base font-medium"
                    style={{ backgroundColor: "#1A237E", color: "#ffffff" }}
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            </div>
          ) : inviteInfo?.isExistingUser ? (
            <div className="w-full md:w-[75%]">
              <div className="mb-8">
                <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Invitation to join{" "}
                  {inviteInfo.organizationName || orgName || "Tempered Talent"}
                </p>
                <h1 className="mt-2 text-3xl font-bold text-foreground">
                  Welcome back, {inviteInfo.firstName || employeeName}!
                </h1>
                <p className="mt-3 text-muted-foreground">
                  You've been invited to join{" "}
                  <strong>{inviteInfo.organizationName}</strong>. Enter your
                  existing password to accept the invitation.
                </p>
              </div>

              <Form {...existingUserForm}>
                <form
                  className="space-y-6"
                  onSubmit={existingUserForm.handleSubmit(
                    handleExistingUserSubmit,
                  )}
                >
                  <FormField
                    control={existingUserForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-foreground">
                          Your password
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              type={showExistingPassword ? "text" : "password"}
                              placeholder="Enter your current password"
                              className="h-11 pr-10 border-border focus:border-blue-500 focus:ring-blue-500"
                              {...field}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowExistingPassword((prev) => !prev)
                              }
                              className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                            >
                              {showExistingPassword ? (
                                <FaEyeSlash />
                              ) : (
                                <FaEye />
                              )}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />

                  {formError && (
                    <p className="text-sm text-red-600 mt-1">{formError}</p>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-11 text-base font-medium"
                    style={{ backgroundColor: "#1A237E", color: "#ffffff" }}
                    disabled={existingUserForm.formState.isSubmitting}
                  >
                    {existingUserForm.formState.isSubmitting
                      ? "Joining..."
                      : "Accept Invitation"}
                  </Button>
                </form>
              </Form>
            </div>
          ) : (
            <div className="w-full md:w-[75%]">
              <div className="mb-8">
                <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Welcome to {orgName || "Tempered Talent"}
                </p>
                <h1 className="mt-2 text-3xl font-bold text-foreground">
                  Hello {employeeName}!
                </h1>
                <p className="mt-3 text-muted-foreground">
                  Enter the temporary password from your invite email and set a
                  new password to access your employee dashboard.
                </p>
              </div>

              <Form {...form}>
                <form
                  className="space-y-6"
                  onSubmit={form.handleSubmit(handleSubmit)}
                >
                  <FormField
                    control={form.control}
                    name="tempPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-foreground">
                          Temporary password
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              type={showTempPassword ? "text" : "password"}
                              placeholder="Paste the code from your email"
                              className="h-11 pr-10 border-border focus:border-blue-500 focus:ring-blue-500"
                              {...field}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowTempPassword((prev) => !prev)
                              }
                              className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                            >
                              {showTempPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="newPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-foreground flex items-center gap-1.5">
                          <span>New password</span>
                          <PasswordRequirementsTooltip />
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              type={showNewPassword ? "text" : "password"}
                              placeholder="Choose a secure password"
                              className="h-11 pr-10 border-border focus:border-blue-500 focus:ring-blue-500"
                              {...field}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowNewPassword((prev) => !prev)
                              }
                              className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                            >
                              {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-foreground">
                          Confirm new password
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              type={showConfirmPassword ? "text" : "password"}
                              placeholder="Re-enter new password"
                              className="h-11 pr-10 border-border focus:border-blue-500 focus:ring-blue-500"
                              {...field}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowConfirmPassword((prev) => !prev)
                              }
                              className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                            >
                              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="agreedPrivacyAndTerms"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="text-sm font-medium leading-none cursor-pointer">
                          I agree to the{" "}
                          <a
                            href="/legal/terms"
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#40C4FF] hover:underline whitespace-nowrap"
                          >
                            Terms & Conditions
                          </a>{" "}
                          and{" "}
                          <a
                            href="/legal/privacy"
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#40C4FF] hover:underline whitespace-nowrap"
                          >
                            Privacy Policy
                          </a>
                        </FormLabel>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />

                  {formError && (
                    <p className="text-sm text-red-600 mt-1">{formError}</p>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-11 text-base font-medium"
                    style={{
                      backgroundColor: "#1A237E",
                      color: "#ffffff",
                    }}
                    disabled={
                      isBusy || !legalDocs?.terms || !legalDocs?.privacy
                    }
                  >
                    {isBusy ? "Finishing setup..." : "Complete Setup"}
                  </Button>
                </form>
              </Form>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
